import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, {FC} from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import {CurrencyYuanOutlined} from '@mui/icons-material';
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {useCreatePayback, useQueryPayback, useUpdatePayback} from "@/service/payback.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {checkQueryKey, emptyValue, iso2Date, setValues} from "@/helper/method";
import {CustomerPO} from "@/model/customer";
import {SearchContractVO} from "@/service/contract.s";
import {isNumber} from "lodash";
import {ProductPO} from "@/model/product";

const validationSchema = z.object({
    customerName: z.string(),
    flag: z.unknown(),
    contractName: z.string().optional(),
    productTypeIds: z.string().optional(),
    remark: z.ostring(),
    id: z.unknown().default(0),
    instanceId: z.number().default(0),
    executeId: z.unknown(),
    contractId: z.number().optional(),
    productId: z.unknown().default(0),
    productName: z.ostring(),
    products: z.any(),
    customerId: z.number({message: "请选择客户名称"}),
    paymentMoney: z.string({message: '请填写回款金额'}).nonempty({message: "请填写回款金额"}),
    paymentDate: z.date({message: "请选择回款时间"}),
    receivingMethod: z.string({message: "请选择到账方式"}),
    receivingMethodId: z.unknown(),
    paymentMethodId: z.unknown(),
    paymentMethod: z.string({message: "请选择回款方式"}),
});

export type WritePaybackForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

interface WritePaybackProps {
    flag: number
}

export const WritePayback: FC<WritePaybackProps> = ({flag}) => {
    const allDict = useAllDict()
    const {paymentMethod, receivingMethod} = allDict
    const {setToast, setDrawerModal} = useGlobalActions()
    const {mutateAsync: createPaybackMutate, isPending: createPaybackPending} = useCreatePayback()
    const {mutateAsync: updatePaybackMutate, isPending: updatePaybackPending} = useUpdatePayback()
    const {onSearch, id: drawerId, write, execute} = useDrawerModal()
    const {data: paybackData} = useQueryPayback({id: drawerId!}, isNumber(drawerId))
    const payback = paybackData?.data
    const initValues = () => {
        if (write == 'edit') {
            return {
                ...payback, paymentDate: iso2Date(payback?.paymentDate)!,
                paymentMoney: payback?.paymentMoney || ''
            }
        }
        return {}
    }
    const {
        register,
        control,
        setValue,
        handleSubmit,
        watch,
        reset,
        formState: {errors},
    } = useForm<WritePaybackForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WritePaybackForm,
        defaultValues,
    });

    const onSubmit = async (values: WritePaybackForm) => {
        const result = write == 'add' ? await createPaybackMutate({...values, flag}) : await updatePaybackMutate({...values, ...execute, flag, id: drawerId})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }
    const [customerId] = watch(["customerId"])

    const BusinessWatched = () => {
        const products: ProductPO[] = useWatch({
            name: 'products',
            control
        })

        return <Grid size={6}>
            <FormControl fullWidth={true} required={true}>
                <FormLabel>业务内容</FormLabel>
                <Controller control={control} render={({field: {onChange, value}}) => {
                    return <Autocomplete
                        options={products && products?.map((item) => ({label: item.category, value: item.id})) || []}
                        value={value || null as any}
                        onChange={((event, option: DictPO | null) => {
                            setValue("productId", option?.value)
                            onChange(option?.label)
                        })}
                        filterSelectedOptions={false}
                        freeSolo={true}
                        fullWidth
                        renderInput={(params) => <TextField
                            onChange={event => onChange(event.target.value)}
                            placeholder={'请选择业务内容'}
                            {...params}/>}
                    />
                }} name={"productName"}/>
            </FormControl>
        </Grid>
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <SearchData<WritePaybackForm, CustomerPO> onSelect={() => {
                        setValues<Pick<WritePaybackForm, "contractId" | "contractName" | "productName" | "productId">>({
                            contractId: undefined,
                            contractName: '',
                            productName: '',
                            productId: ''
                        }, setValue)
                    }} control={control} attr={"customerName"}
                                                              attrId={"customerId"} errMsg={errors?.customerId?.message} setValue={setValue}
                                                              label={"客户名称"}/>
                </Grid>
                <Grid size={6}>
                    <SearchData<WritePaybackForm, SearchContractVO>
                        readOnly={!checkQueryKey(customerId)} onSelect={(item: SearchContractVO) => {
                        setValues<Pick<WritePaybackForm, "productName" | "productId" | "productTypeIds" | "products">>({
                            productName: '',
                            productId: undefined,
                            productTypeIds: item?.productTypeIds,
                            products: item?.products || []
                        }, setValue)
                    }} autoLoad={true} query={{customerId}} control={control} tag={2} attr={"contractName"} attrId={"contractId"}
                        setValue={setValue} label={"关联合同"}/>
                </Grid>
                <BusinessWatched/>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>{flag == -1 ? '预计' : ''}回款金额</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.paymentMoney}
                            helperText={errors?.paymentMoney?.message}
                            type={'number'}
                            value={value || undefined}
                            onChange={event => onChange(event.target.value)}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 1000000,
                                        maxLength: 8, inputMode: 'numeric', pattern: '/^[0-9.]*$/'
                                    },
                                    endAdornment: <CurrencyYuanOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="填写回款金额"
                            variant="outlined"
                        />} name={'paymentMoney'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="paymentDate">{flag == -1 ? '预计' : ''}回款时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                id='account-settings-date'
                                locale='zhCN'
                                placeholderText={"选择回款时间"}
                                customInput={<TextField
                                    error={!!errors?.paymentDate}
                                    helperText={errors?.paymentDate?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"paymentDate"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>回款方式</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={paymentMethod}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue(`paymentMethodId`, option?.value);
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.paymentMethod}
                                           helperText={errors?.paymentMethod?.message}
                                           variant="outlined"
                                           placeholder={"选择回款方式"} {...params} />
                            )}
                        />} name={"paymentMethod"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>到账方式</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={receivingMethod}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue(`receivingMethodId`, option?.value);
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.receivingMethod}
                                           helperText={errors?.receivingMethod?.message}
                                           variant="outlined"
                                           placeholder={"选择到账方式"} {...params} />
                            )}
                        />} name={"receivingMethod"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel htmlFor="remark">备注</FormLabel>
                        <TextField
                            id="remark"
                            multiline
                            {...register("remark")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto'},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            placeholder="输入备注"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createPaybackPending || updatePaybackPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
